import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { SortColumn } from 'src/app/models/sortColumn.model';
import { StoreService } from 'src/app/services/infra/store/store.service';
import { ISelectOption } from '../../../selectOptions';
import { FilterTypesEnum } from '../../filterTypes.enum';
import { IPageFilters } from '../../interfaces/pageFilters.interface';
import { ISelectedFilters } from '../../selectedFilters.interface';
import { MultiSelectFilters } from '../filtersTypes/multiFilter.model';
import { FiltersQueryTransformer } from 'src/app/services/filters/filtersQueryTransformer.ts/filtersQueryTransformer';
import { IntegrationModel } from 'src/app/models/api';
import { fromIntegrationToSelectOption } from './utils';

import { EnterpriseAiDetectionTabsEnum } from 'src/app/views/enterprise-ai/enterprise-ai-detection-tabs-management/enterprise-ai-detection-tabs-enum';

export class AiDataInsightsFiltersModel implements IPageFilters {
  public onUpdate$: BehaviorSubject<ISelectedFilters>;
  public allOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>([]);
  public integrationFilters: MultiSelectFilters;
  public sortedColumn$: BehaviorSubject<SortColumn | undefined> = new BehaviorSubject<SortColumn | undefined>(
    undefined
  );

  constructor(
    private readonly _router: Router,
    private readonly _store: StoreService
  ) {
    this._initializeFilters();
    this._integrationsOnUpdate();

    this.onUpdate$ = new BehaviorSubject(this.getSelectedFilters());
  }

  public getSelectedFilters(): ISelectedFilters {
    return {
      integration: this.integrationFilters.getSelectedFiltersValues(),
      search: 'ZN_M00001',
    };
  }

  public getAllFiltersOptions(): BehaviorSubject<ISelectOption[]> {
    return this.allOptions$;
  }

  public getAllSelectedOptions(): ISelectOption[] {
    const selectedOptions = [...this.integrationFilters.getSelectedOptions()];

    return selectedOptions;
  }

  public updateFilterFromUserSelection(
    filterToUpdate: FilterTypesEnum | undefined,
    value: ISelectOption[] | Date | boolean | string | undefined
  ) {
    let needUpdate = false;
    if (value instanceof Array) {
      if (value.length == 0) {
        needUpdate = this.clearAllMultiSelectFiltersIfNeeded();
      } else {
        const optionsByCategory: {
          [key: string]: ISelectOption[];
        } = {};
        value.forEach((option) => {
          if (option.filterType) {
            if (option.filterType in optionsByCategory) {
              optionsByCategory[option.filterType].push(option);
            } else {
              optionsByCategory[option.filterType] = [option];
            }
          }
        });
        this._updateAllOptions();

        needUpdate =
          this.integrationFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.Integration] || []) || needUpdate;
      }
    }
    if (needUpdate) {
      const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
      this._router.navigate([], {
        queryParams: {
          tab: EnterpriseAiDetectionTabsEnum.DataInsights,
          q: filtersQuery,
        },
        queryParamsHandling: 'merge',
      });
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  public getFilterOptionsIfExist$(filter: FilterTypesEnum): BehaviorSubject<ISelectOption[]> {
    switch (filter) {
      case FilterTypesEnum.Integration:
        return this.integrationFilters.getOptions$();

      default:
        throw new Error(`AiDataInsightsFiltersModel getFilterOptionsIfExist: ${filter} not supported`);
    }
  }

  public removeSelectedOptionsIfExist(options: ISelectOption[]): void {
    const optionsIdsByCategory: { [key: string]: Set<string> } = {};
    options.forEach(
      (option) => {
        if (option.filterType) {
          if (option.filterType in optionsIdsByCategory) {
            optionsIdsByCategory[option.filterType].add(option.id);
          } else {
            optionsIdsByCategory[option.filterType] = new Set([option.id]);
          }
        }
      },
      this.getDynamicFilters().forEach((filter) => {
        optionsIdsByCategory[filter] = new Set();
      })
    );

    if (optionsIdsByCategory[FilterTypesEnum.Integration]) {
      this.integrationFilters.updateIfNeeded(
        this.integrationFilters
          .getSelectedOptions()
          .filter((selectedOption) => !optionsIdsByCategory[FilterTypesEnum.Integration].has(selectedOption.id))
      );
    }

    const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
    this._router.navigate([], {
      queryParams: { tab: 'dataInsights', q: filtersQuery },
      queryParamsHandling: 'merge',
    });
    this.onUpdate$.next(this.getSelectedFilters());
  }

  public getFilterSelectedIfExist(filter: FilterTypesEnum): string[] {
    switch (filter) {
      case FilterTypesEnum.Integration:
        return this.integrationFilters.getSelectedFiltersIds();

      default:
        throw new Error(`AiDataInsightsFiltersModel getFilterSelectedIfExist: ${filter} not supported`);
    }
  }

  public updateFiltersByKeyValue(keyValueFilters: { [key: string]: string[] }, updateQueryParams = false): void {
    let needUpdate = false;

    let values: string[] = [];

    values = 'integration' in keyValueFilters ? keyValueFilters['integration'] : [];
    needUpdate = this.integrationFilters.updateByValuesIfNeeded(values) || needUpdate;

    if (needUpdate) {
      if (updateQueryParams) {
        const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
        this._router.navigate([], {
          queryParams: { tab: 'activity', q: filtersQuery },
          queryParamsHandling: 'merge',
        });
      }
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  public clearAllMultiSelectFiltersIfNeeded(): boolean {
    return this.integrationFilters.updateIfNeeded([]);
  }

  public getDynamicFilters(): string[] {
    return [];
  }

  private _initializeFilters() {
    this.integrationFilters = new MultiSelectFilters([]);
    this._updateAllOptions();
  }

  private _updateAllOptions() {
    this.allOptions$.next([...this.integrationFilters.getOptions$().value]);
  }

  private _integrationsOnUpdate() {
    this._store.integrations$.subscribe((integrations: IntegrationModel[]) => {
      const integrationsPlatformSet: Set<string> = new Set();

      const integrationsOptionsMap: ISelectOption[] = integrations.map((integration) => {
        integrationsPlatformSet.add(integration.platform);
        return fromIntegrationToSelectOption(integration);
      });

      const shouldUpdateSelectedOptions = this.integrationFilters.updateOptions(Object.values(integrationsOptionsMap));
      this._updateAllOptions();
      if (shouldUpdateSelectedOptions) {
        this.onUpdate$.next(this.getSelectedFilters());
      }
    });
  }
}
